import { isNumber, keyBy } from 'lodash-es';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LocationService } from 'services';
import { taskFilters, tasksStatusesIds } from 'settings/constants/transactionTasks';
import {
  resetTransactionTasksAggregateEffect,
  setTaskFiltersAppliedEffect,
  setTaskFiltersEffect,
  setTransactionsTasksFilterEffect,
} from 'store/effects';
import { getTaskAggregateEffect, resetTaskAggregateEffect } from 'store/effects/taskAggregate';
import { getFilteredTaskAggregateSelector } from 'store/selectors/taskAggregate';
import { getTransactionAccessByTaskSelector } from 'store/selectors/transaction';
import { getIsTaskFiltersApplied } from 'store/selectors/transactionTasks';

export const useFetchTasks = ({ selectedTaskIds }) => {
  const dispatch = useDispatch();

  const { hasFullAccess } = useSelector(getTransactionAccessByTaskSelector);
  const { tasks, isPending } = useSelector(getFilteredTaskAggregateSelector);
  const filtersApplied = useSelector(getIsTaskFiltersApplied);

  const [loading, setLoading] = useState(true);

  const locationSrv = new LocationService();
  locationSrv.setLocation(location);
  const query = locationSrv.getQuery();

  useEffect(() => {
    let filters = {};

    let transactionTaskFilter = taskFilters.allTasks;
    setLoading(true);

    if ('teamStats' in query) {
      if (!query.teamStats) {
        filters = { assignee: 'me' };
        transactionTaskFilter = taskFilters.myTasks;
      }

      if ('status' in query || 'dueToday' in query || query?.taskIds) {
        filters = {
          ...filters,
          ...('status' in query && { statuses: [query.status] }),
          ...('dueToday' in query && {
            dueFrom: moment().format('YYYY-MM-DD'),
            dueTo: moment().format('YYYY-MM-DD'),
            statuses: [
              tasksStatusesIds.new,
              tasksStatusesIds.inProgress,
              tasksStatusesIds.stuck,
              tasksStatusesIds.na,
              tasksStatusesIds.done,
            ],
          }),
          campaignOnly: false,
          ...(query?.taskIds && {
            taskIds: isNumber(query.taskIds) ? [query.taskIds] : query.taskIds.split(','),
            statuses: [
              tasksStatusesIds.new,
              tasksStatusesIds.inProgress,
              tasksStatusesIds.stuck,
              tasksStatusesIds.na,
              tasksStatusesIds.done,
              tasksStatusesIds.overdue,
            ],
          }),
        };
        dispatch(setTaskFiltersEffect(filters));
        dispatch(setTaskFiltersAppliedEffect(true));
        dispatch(setTransactionsTasksFilterEffect(transactionTaskFilter));
      }
    }

    return () => {
      dispatch(resetTaskAggregateEffect());
      dispatch(setTaskFiltersAppliedEffect(false));
      dispatch(resetTransactionTasksAggregateEffect());
    };
  }, []);

  const fetchAggregateTasks = (ids?: number[], filters = {}) =>
    dispatch(
      getTaskAggregateEffect({
        ids,
        filters,
      }),
    );

  const tasksById = useMemo(() => keyBy(tasks || [], 'Id'), [tasks]);

  const fullAccess = useMemo(() => {
    return selectedTaskIds?.every((taskId) => {
      const task = tasksById[taskId];
      return task && hasFullAccess(task?.Transaction?.Id);
    });
  }, [tasksById, selectedTaskIds]);

  return {
    fullAccess,
    fetchAggregateTasks,
    isPending: isPending || !tasks,
    filtersApplied,
  };
};
